import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { Scene } from '@babylonjs/core/scene';

import type { AoeShape } from '../net/protocol';

/**
 * The footprint a cast's area draws on the floor, one mesh per shape.
 *
 * ── Why the shape has to be drawn and not implied ────────────────────────────
 *
 * Every area effect used to be one expanding torus, whatever the skill was, because the
 * server only ever sent a radius. So Ground Slam's *"90-degree frontal cone"* drew a full
 * circle at the cursor, and Fan of Knives' *"360-degree circle around the player"* drew
 * nothing at all, its radius being zero.
 *
 * The damage now lands in the shape the description promised (`inShape` in `combat.go`), and
 * **the drawing has to agree or it is a lie about where the damage went** — which is worse
 * than no drawing, because a player learns to aim from it. A ring where a cone went off
 * teaches the wrong lesson every single cast.
 *
 * ── Flat, unlit, unpickable ──────────────────────────────────────────────────
 *
 * All three are the same rule the old torus followed. Flat on the floor, because this is a
 * decal and a shape standing up would read as a wall. Unlit, because it is light rather than
 * a surface — the material is emissive and the glow layer blooms it. Unpickable, because it
 * is drawn over the floor a fight is happening on and a shape that swallowed clicks would
 * eat them from the monsters standing inside it.
 *
 * Every mesh is built at **unit extent** and scaled to the radius by the caller, so one
 * geometry serves every size and the animation is a single `scaling` tween.
 */

/** How thick a ring's line is, as a fraction of its radius. */
const RING_THICKNESS = 0.07;

/** How wide a LINE's corridor is — `LineHalfWidth` in Go, doubled. */
export const LINE_WIDTH = 2.4;

/** Segments around a full circle. Enough that an arc has no visible facets at this size. */
const TESSELLATION = 48;

/**
 * Builds the footprint for a shape, or null when the shape covers no ground.
 *
 * `SELF` and `NONE` return null on purpose: a buff has no area, and drawing a ring for one
 * would tell the player something reached out when nothing did.
 *
 * The mesh is positioned and scaled by the caller — `origin` here only decides *where the
 * geometry sits relative to its own pivot*, which is the difference between a circle (centred)
 * and a cone or a corridor (starting at the pivot and running along +Z).
 */
export function buildAoeMesh(scene: Scene, shape: AoeShape, angleDeg: number): Mesh | null {
  switch (shape) {
    case 'TARGET_RADIAL':
    case 'SELF_RADIAL':
      // A torus rather than a disc: an outline reads as an *extent*, and a filled circle
      // over the floor hides whatever is standing in it — which is what the player is
      // trying to look at. Diameter 2 so scaling by the radius puts the edge on it.
      return MeshBuilder.CreateTorus(
        'aoe-ring',
        { diameter: 2, thickness: RING_THICKNESS * 2, tessellation: TESSELLATION },
        scene,
      );

    case 'FRONTAL_CONE':
      return buildWedge(scene, angleDeg);

    case 'LINE':
      // A unit-length corridor running along +Z from the pivot, so scaling Z by the length
      // grows it forward rather than from the middle. Built at full width because the
      // corridor's width is fixed in world units and must not scale with its length.
      return buildCorridor(scene);
  }
  return null;
}

/**
 * A filled arc, flat on the floor, opening along +Z from its pivot.
 *
 * A ribbon rather than a disc with a hole: the arc has to be *filled* to read as a sweep,
 * and a cone drawn as an outline looks like a targeting reticle for a shape rather than the
 * shape itself. Half the angle either side of +Z, matching `inShape` — the angle is the full
 * width, so a 90-degree cone is 45 each way.
 */
function buildWedge(scene: Scene, angleDeg: number): Mesh {
  const half = ((angleDeg / 2) * Math.PI) / 180;
  const steps = Math.max(6, Math.round((TESSELLATION * angleDeg) / 360));

  // Fan of triangles from the pivot: the caster's feet, then the arc.
  const positions: number[] = [0, 0, 0];
  const uvs: number[] = [0.5, 0.5];
  const indices: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = -half + (2 * half * i) / steps;
    const x = Math.sin(a);
    const z = Math.cos(a);
    positions.push(x, 0, z);
    // **The unit circle mapped onto the sprite's square.** The atlas's ground cells are all
    // circular — a fire ring, an ice ring, a void portal — so sampling them in polar terms
    // means a wedge shows exactly the arc of the ring it covers. A flat quad mapping would
    // stretch a whole ring into the cone and read as a smear.
    uvs.push((x + 1) / 2, (z + 1) / 2);
    if (i > 0) indices.push(0, i, i + 1);
  }

  return applyGeometry(scene, 'aoe-cone', positions, indices, uvs);
}

/**
 * Turns a triangle soup into a mesh.
 *
 * Hand-built `VertexData` rather than a `MeshBuilder` primitive, because none of them is an
 * arc: `CreateDisc` has no angle, `CreateCylinder` has an `arc` but is a solid with walls and
 * caps, and a ribbon wants a grid of paths. Two flat shapes are a dozen vertices — cheaper to
 * state outright than to talk a builder into.
 *
 * `sideOrientation: DOUBLESIDE` because the camera is above and a single-sided floor decal
 * disappears the moment anything looks at its back — the same reason the old torus set
 * `backFaceCulling = false` on its material.
 */
function applyGeometry(
  scene: Scene,
  name: string,
  positions: number[],
  indices: number[],
  uvs: number[],
): Mesh {
  const mesh = new Mesh(name, scene);
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  // **Required, and silently wrong when absent.** Without a UV set every vertex samples the
  // same texel, so a textured footprint renders as one flat colour taken from a corner of
  // the atlas — which looks like a material bug rather than missing geometry data.
  data.uvs = uvs;
  // Straight up: these lie on the floor, so every normal is the same and computing them
  // from the geometry would be arithmetic with one answer.
  data.normals = Array.from({ length: positions.length / 3 }, () => [0, 1, 0]).flat();
  data.applyToMesh(mesh);
  mesh.sideOrientation = Mesh.DOUBLESIDE;
  return mesh;
}

/** A unit-length, fixed-width corridor running along +Z from its pivot. */
function buildCorridor(scene: Scene): Mesh {
  const w = LINE_WIDTH / 2;
  const positions = [-w, 0, 0, w, 0, 0, w, 0, 1, -w, 0, 1];
  const indices = [0, 1, 2, 0, 2, 3];
  // The sprite runs the length of the corridor: across is u, along is v.
  const uvs = [0, 0, 1, 0, 1, 1, 0, 1];
  return applyGeometry(scene, 'aoe-line', positions, indices, uvs);
}
